import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  isNetworkError?: boolean;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in child component tree
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if it's a network-related error
    const isNetworkError = 
      error.message?.includes('NetworkError') ||
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('Load failed') ||
      error.message?.includes('Network request failed') ||
      error.message?.includes('timeout');
    
    return { 
      hasError: true, 
      error,
      isNetworkError 
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console or error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Store error info in state
    this.setState({ errorInfo });
    
    // In production, you could send this to error monitoring service
    // Example: Sentry.captureException(error);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            
            <h2 className="mt-4 text-xl font-semibold text-center text-gray-900">
              {this.state.isNetworkError ? '❌ Kesalahan Jaringan' : 'Oops! Terjadi Kesalahan'}
            </h2>
            
            <p className="mt-2 text-sm text-center text-gray-600">
              {this.state.isNetworkError 
                ? 'Terjadi masalah koneksi jaringan. Periksa internet Anda dan coba lagi.'
                : 'Maaf, aplikasi mengalami error yang tidak terduga.'
              }
            </p>
            
            {this.state.error && (
              <div className="mt-4 p-3 bg-gray-100 rounded text-xs font-mono text-gray-700 overflow-auto max-h-32">
                {this.state.error.toString()}
              </div>
            )}
            
            {this.state.isNetworkError && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                <strong>💡 Solusi:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Periksa koneksi internet Anda</li>
                  <li>Coba gunakan DNS 8.8.8.8 atau 1.1.1.1</li>
                  <li>Periksa status Cloudflare/server</li>
                  <li>Clear cache browser dan reload</li>
                </ul>
              </div>
            )}
            
            <div className="mt-6 flex gap-3">
              <button
                onClick={this.handleReset}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
              >
                Muat Ulang Halaman
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 transition"
              >
                Kembali ke Beranda
              </button>
            </div>
            
            <p className="mt-4 text-xs text-center text-gray-500">
              Jika masalah berlanjut, hubungi administrator sistem.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
